import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Button } from '../components/Button.tsx';
import { SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadAudit, loadTenants, type AdminTenant, type AuditRow } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const PAGE = 100;

/** Families of recorded actions, for the filter. */
const ACTIONS: ReadonlyArray<readonly [string, string]> = [
  ['', 'Every change'],
  ['licence.', 'Licences, seats and devices'],
  ['invoice.', 'Invoices'],
  ['service.', 'Service switches'],
  ['tenant.', 'Organisation changes'],
  ['security.', 'Two-step sign-in'],
  ['export.', 'Exports'],
  ['membership.', 'Staff and roles'],
  ['client_membership.', 'Client users'],
  ['platform.', 'Teslyn settings'],
];

const time = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** One line of detail, short: the recorded JSON without its braces. */
function detailText(d: unknown): string {
  if (!d || typeof d !== 'object' || Object.keys(d).length === 0) return '';
  const text = JSON.stringify(d);
  return text.length > 160 ? `${text.slice(0, 157)}…` : text;
}

/** The audit rows as a table: Braynewave's console (every organisation) and a company's own Audit log page (R166). */
export function AuditTable({
  rows,
  showTenant,
}: {
  readonly rows: readonly AuditRow[];
  readonly showTenant: boolean;
}): ReactElement {
  if (rows.length === 0) return <p className="body muted">Nothing recorded.</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th className="label">When</th>
            {showTenant && <th className="label">Organisation</th>}
            <th className="label">Who</th>
            <th className="label">What</th>
            <th className="label">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="caption">{time(r.at)}</td>
              {showTenant && <td>{r.tenantName}</td>}
              <td className="caption data">{r.actorName ?? r.actorEmail ?? 'Teslyn'}</td>
              <td className="data">
                {r.action}
                {r.target ? <span className="caption muted audit-target"> {r.target}</span> : null}
              </td>
              <td className="caption data">{detailText(r.detail)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Braynewave's view of the audit log across organisations, newest first. */
export function AdminAuditScreen({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [action, setAction] = useState('');
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTenants(sb)
      .then(setTenants)
      .catch(() => setTenants([]));
  }, [sb]);

  const load = useCallback(
    (before: number | null) => {
      setError(null);
      loadAudit(sb, { tenantId: tenantId || null, action, before, limit: PAGE })
        .then((r) => {
          setRows((old) => (before === null ? r : [...(old ?? []), ...r]));
          setMore(r.length === PAGE);
        })
        .catch((e: unknown) => setError(message(e)));
    },
    [sb, tenantId, action],
  );
  useEffect(() => {
    setRows(null);
    load(null);
  }, [load]);

  return (
    <section className="page">
      <PageHeader title="Audit log" subtitle="Every recorded change, newest first." />
      <div className="form-grid">
        <SelectField
          label="Organisation"
          value={tenantId}
          onChange={setTenantId}
          options={[['', 'Every organisation'], ...tenants.map((t) => [t.id, t.name] as const)]}
        />
        <SelectField label="Kind of change" value={action} onChange={setAction} options={ACTIONS} />
      </div>
      {error && <ErrorState message={error} onRetry={() => load(null)} />}
      {!rows && !error && <Loading what="the audit log" />}
      {rows && <AuditTable rows={rows} showTenant={!tenantId} />}
      {rows && more && (
        <div className="row">
          <Button onClick={() => load(rows[rows.length - 1]?.id ?? null)}>Show older</Button>
        </div>
      )}
    </section>
  );
}
